/**
 * Camlin Companion — proactive nudge bubble (doc 116 §2.4).
 *
 * "Escalation ladder, opt-in shaped: the avatar badge pulses → a short text
 * bubble (...) → conversation only after the visitor clicks." This
 * component is the middle rung: a small, dismissable text bubble anchored
 * next to the badge. It never opens the chat panel itself — clicking the
 * bubble text or the badge does, dismissing (✕) the bubble does not, and
 * ONLY the ✕ persists the dismiss (per-visitor, cool-off) via `onDismiss`.
 */

import React from 'react';
import { CAMLIN_COMPANION_ATTR } from './attributes';

export interface CompanionNudgeBubbleProps {
  message: string;
  onOpen: () => void;
  onDismiss: () => void;
}

const shellStyle: React.CSSProperties = {
  position: 'fixed',
  bottom: '88px',
  right: '20px',
  maxWidth: '260px',
  zIndex: 9998,
  display: 'flex',
  alignItems: 'flex-start',
  gap: '6px',
  background: 'rgba(15,23,42,0.92)',
  color: '#e2e8f0',
  borderRadius: '14px 14px 2px 14px',
  padding: '10px 12px',
  boxShadow: '0 12px 32px rgba(15,23,42,0.3)',
  fontFamily:
    'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  fontSize: '13px',
  lineHeight: 1.4,
  cursor: 'pointer'
};

const dismissButtonStyle: React.CSSProperties = {
  border: 'none',
  background: 'transparent',
  color: 'rgba(226,232,240,0.6)',
  cursor: 'pointer',
  fontSize: '14px',
  lineHeight: 1,
  padding: '2px'
};

export function CompanionNudgeBubble({ message, onOpen, onDismiss }: CompanionNudgeBubbleProps) {
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={message}
      {...{ [CAMLIN_COMPANION_ATTR.nudge]: 'bubble' }}
      style={shellStyle}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          onOpen();
        }
      }}
    >
      <span style={{ flex: 1 }}>{message}</span>
      <button
        type="button"
        aria-label="Dismiss"
        style={dismissButtonStyle}
        onClick={(event) => {
          event.stopPropagation();
          onDismiss();
        }}
      >
        ✕
      </button>
    </div>
  );
}

export default CompanionNudgeBubble;
